import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hudTomeTokens } from '../../HUD/useHudTomeVibe';
import { zoneTomeVibe } from '../../primitives/tome/zoneTomeVibes';
import { windowBridge } from '../../framework/windowBridge';
import { WorldMapView, type WorldMapViewProps } from './WorldMapView';
import { atlasMatchesModel, watercolorArtworkUrl, watercolorAtlas as atlas } from './watercolorAtlas';
import { resolveMapPresentationId } from './worldMapModel';
import { WorldMapKnownPlaceNotes } from './WorldMapKnownPlaceNotes';
import { WorldMapQuestNotes } from './WorldMapQuestNotes';
import type { Helix2000Character } from '../../../../shared/CharacterTypes';
import { WatercolorFogLayer } from './WatercolorFogLayer';
import { WatercolorFrontierLayer } from './WatercolorFrontierLayer';
import type { FrontierRoad } from './worldMapFrontier';
import { placeVisibility, type WorldMapFog } from './worldMapFog';
import { consumeAtlasFocus, type AtlasFocusRequest } from './atlasFocus';
import './WatercolorWorldMap.css';

export const WatercolorWorldMap: React.FC<WorldMapViewProps & {
  fog?: WorldMapFog | null; frontier?: readonly FrontierRoad[]; focusRequest?: AtlasFocusRequest | null;
  /** The character whose active quests the strip lists for the shown place. */
  player?: Helix2000Character | null;
}> = ({
  model, currentMapId, knownPlaces, fog = null, frontier = [], focusRequest = null, player = null,
}) => {
  const presentationMapId = resolveMapPresentationId(currentMapId) ?? null;
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [imageFailed, setImageFailed] = useState(false);
  const sheet = useRef<HTMLDivElement>(null);
  const artwork = useRef<SVGImageElement>(null);
  const byId = new Map(model.places.map(place => [place.id, place]));
  const current = presentationMapId ? byId.get(presentationMapId) : undefined;
  const shown = byId.get(hovered ?? selected ?? '') ?? current;
  const visibility = (id: string) => placeVisibility(fog, id);
  const shownRumoured = !!shown && visibility(shown.id) === 'rumoured';
  const listed = model.places.filter(place => visibility(place.id) === 'charted');
  const tokens = hudTomeTokens(zoneTomeVibe(current?.id));

  useEffect(() => {
    const image = artwork.current;
    const failed = () => setImageFailed(true);
    image?.addEventListener('error', failed);
    return () => image?.removeEventListener('error', failed);
  }, [model, imageFailed]);

  // A place another window asked to show (atlasFocus.ts) wins over following the current map.
  const asked = useRef(false);
  useEffect(() => { if (!asked.current) setSelected(presentationMapId); }, [presentationMapId]);
  useEffect(() => {
    if (!focusRequest) return;
    if (byId.has(focusRequest.mapId)) {
      asked.current = true;
      setSelected(focusRequest.mapId);
    }
    consumeAtlasFocus();
  }, [focusRequest?.seq]); // once per request, not per render of byId

  const centerPlace = useCallback((id: string) => {
    const place = atlas.places.find(row => row.id === id);
    const box = sheet.current;
    if (!place || !box) return;
    // Artwork and overlays share this crop of the 1600x1000 exported frame.
    box.scrollLeft = (place.x - 35) / 1530 * box.scrollWidth - box.clientWidth / 2;
    box.scrollTop = (place.y - 125) / 793 * box.scrollHeight - box.clientHeight / 2;
  }, []);

  useLayoutEffect(() => {
    const id = selected ?? presentationMapId;
    if (id) centerPlace(id);
  }, [selected, presentationMapId, zoom, centerPlace]);

  const focusPlace = (id: string) => {
    setSelected(id);
    centerPlace(id);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (selected && selected !== presentationMapId) {
        event.preventDefault();
        event.stopPropagation();
        asked.current = false;
        setSelected(presentationMapId);
      } else {
        event.preventDefault();
        windowBridge.close('worldMap');
      }
    }
  };

  if (imageFailed || !atlasMatchesModel(model)) {
    // The schematic fallback draws no fog, so under fog it gets only the charted places and their roads.
    const fallback = fog ? { ...model, places: listed,
      roads: model.roads.filter(road => fog.charted.has(road.from) && fog.charted.has(road.to)) } : model;
    return <WorldMapView model={fallback} currentMapId={currentMapId} knownPlaces={knownPlaces} />;
  }

  return (
    <div
      className={`world-map watercolor-world-map ${tokens.className}`}
      style={tokens.style}
      data-world-map-current={current?.id ?? ''}
      data-world-map-art="watercolors"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="watercolor-map-controls">
        <div className="watercolor-map-index-wrap">
          <label htmlFor="watercolor-map-place-select" className="watercolor-map-index-label">Find place</label>
          <select
            id="watercolor-map-place-select"
            aria-label="Find a place"
            className="watercolor-map-select"
            value={selected ?? ''}
            onChange={event => focusPlace(event.target.value)}
          >
            <option value="">Places ({listed.length})…</option>
            {listed.map(place => (
              <option key={place.id} value={place.id}>{place.name}</option>
            ))}
          </select>
        </div>
        <div className="watercolor-map-actions">
          <button
            type="button"
            className="watercolor-map-btn"
            onClick={() => setZoom(value => Math.max(1, value - 0.5))}
            aria-label="Zoom out"
            title="Zoom out"
          >−</button>
          <button
            type="button"
            className="watercolor-map-btn"
            onClick={() => { setZoom(1); sheet.current?.scrollTo?.(0, 0); }}
            aria-label="Fit world map"
            title="Fit world map"
          >Fit</button>
          <button
            type="button"
            className="watercolor-map-btn"
            onClick={() => setZoom(value => Math.min(3, value + 0.5))}
            aria-label="Zoom in"
            title="Zoom in"
          >+</button>
          <button
            type="button"
            className="watercolor-map-btn watercolor-map-btn--location"
            disabled={!current}
            onClick={() => current && focusPlace(current.id)}
            title={current ? `Center on ${current.name}` : 'Current location unknown'}
          >
            <span className="watercolor-location-dot" aria-hidden="true" />
            My location
          </button>
        </div>
      </div>
      <div className="world-map-sheet watercolor-map-sheet" ref={sheet}>
        <svg
          className="watercolor-map-svg"
          viewBox="35 125 1530 793"
          style={{ width: `${zoom * 100}%` }}
          role="img"
          aria-label={`${model.title} world map${current ? `, you are in ${current.name}` : ''}`}
        >
          <image
            ref={artwork}
            className="watercolor-map-art"
            href={watercolorArtworkUrl}
            x={0}
            y={0}
            width={1600}
            height={1000}
            aria-hidden="true"
          />
          {fog && <WatercolorFogLayer fog={fog} anchors={atlas.places} />}
          <WatercolorFrontierLayer roads={frontier.filter(road => visibility(road.from) === 'charted')} anchors={atlas.places} />
          <g className="watercolor-map-route-highlights" aria-hidden="true">
            {atlas.routes.filter(road => (road.from === shown?.id || road.to === shown?.id)
              && visibility(road.from) !== 'hidden' && visibility(road.to) !== 'hidden').map(road => (
              <polyline key={road.id} points={road.points.map(point => `${point.x},${point.y}`).join(' ')} />
            ))}
          </g>
          <g className="world-map-places">
            {atlas.places.map(anchor => {
              const place = byId.get(anchor.id)!;
              const seen = visibility(place.id);
              if (seen === 'hidden') return null;
              const rumoured = seen === 'rumoured';
              const name = rumoured ? 'Uncharted place' : place.name;
              const band = rumoured ? '' : place.band;
              const isCurrent = current?.id === place.id;
              const isSelected = (selected ?? current?.id) === place.id;
              return (
                <g
                  key={place.id}
                  className={`world-map-place${rumoured ? ' world-map-place--rumoured' : ''}`}
                  data-place={place.id}
                  data-place-fog={fog ? seen : undefined}
                  data-place-current={isCurrent || undefined}
                  data-place-selected={isSelected || undefined}
                  tabIndex={0}
                  role="button"
                  aria-pressed={isSelected}
                  aria-label={`${name}${band ? `, ${band}` : ''}${isCurrent ? ', you are here' : ''}`}
                  onPointerEnter={() => setHovered(place.id)}
                  onPointerLeave={() => setHovered(null)}
                  onClick={() => focusPlace(place.id)}
                  onFocus={() => setHovered(place.id)}
                  onBlur={() => setHovered(null)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      focusPlace(place.id);
                    }
                  }}
                >
                  <title>{name}{band ? ` · ${band}` : ''}</title>
                  <circle className="world-map-hit" cx={anchor.x} cy={anchor.y} r={30} />
                  <circle className="watercolor-map-selection" cx={anchor.x} cy={anchor.y} r={25} />
                  {isCurrent && <circle className="world-map-here-ring" cx={anchor.x} cy={anchor.y} r={21} />}
                  <text className="watercolor-map-label" x={anchor.label.x} y={anchor.label.y} textAnchor="middle">{rumoured ? '?' : place.name}</text>
                </g>
              );
            })}
          </g>
          {!fog && <text className="watercolor-map-sea-label" x={800} y={900} textAnchor="middle">Sea Passage</text>}
        </svg>
      </div>
      <footer className="world-map-strip" aria-live="polite">
        {shown ? (
          <>
            <strong className="world-map-strip-name">{shownRumoured ? 'Uncharted place' : shown.name}</strong>
            {!shownRumoured && shown.band && <span className="world-map-strip-band">{shown.band}</span>}
            <span>{shownRumoured ? 'A road leads here from somewhere you have been' : shown.onMainRoute ? 'On the main road' : 'Side path'}</span>
            {shown.id === current?.id && <span className="world-map-strip-here">You are here</span>}
            {!shownRumoured && <WorldMapKnownPlaceNotes places={knownPlaces?.[shown.id]} currentMapId={currentMapId} />}
            {!shownRumoured && <WorldMapQuestNotes player={player} placeId={shown.id} />}
            {selected && selected !== current?.id && (
              <button
                type="button"
                className="watercolor-map-dismiss-selection"
                onClick={() => setSelected(presentationMapId)}
                title="Clear selection (Esc)"
                aria-label="Clear selection"
              >
                Clear
              </button>
            )}
          </>
        ) : (
          <span>You are off these roads. Choose a place to explore the world.</span>
        )}
        <span className="watercolor-map-hint">Select a place to inspect · Scroll to pan</span>
      </footer>
    </div>
  );
};
